import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Check,
  ChevronRight,
  CircleUserRound,
  Eye,
  EyeOff,
  FileText,
  LoaderCircle,
  LogOut,
  Menu,
  Moon,
  NotebookPen,
  PanelLeftClose,
  PanelLeftOpen,
  Pin,
  Plus,
  Search,
  Sparkles,
  Sun,
  Trash2,
  X,
} from 'lucide-react';
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, type Note, type User } from './api';

type View = 'notes' | 'pinned' | 'archive';
type Theme = 'light' | 'dark';
type SaveState = 'idle' | 'saving' | 'saved' | 'error';

function App() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem('note-theme');
    if (stored === 'light' || stored === 'dark') return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('note-theme', theme);
  }, [theme]);

  useEffect(() => {
    api.auth.me().then(({ user: currentUser }) => setUser(currentUser)).catch(() => setUser(null));
  }, []);

  if (user === undefined) return <BootScreen />;
  if (!user) return <AuthScreen onAuthenticated={setUser} theme={theme} setTheme={setTheme} />;
  return <NotesWorkspace user={user} onLogout={() => setUser(null)} theme={theme} setTheme={setTheme} />;
}

function BootScreen() {
  return (
    <div className="boot-screen" role="status" aria-label="正在加载">
      <BrandMark />
      <LoaderCircle className="spin" size={18} />
    </div>
  );
}

function AuthScreen({
  onAuthenticated,
  theme,
  setTheme,
}: {
  onAuthenticated: (user: User) => void;
  theme: Theme;
  setTheme: (theme: Theme) => void;
}) {
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await api.auth.login({ account, password, remember });
      onAuthenticated(result.user);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '登录失败，请稍后重试');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-layout">
      <button
        className="icon-button theme-button"
        type="button"
        title={theme === 'light' ? '切换深色模式' : '切换浅色模式'}
        aria-label={theme === 'light' ? '切换深色模式' : '切换浅色模式'}
        onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
      >
        {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
      </button>

      <section className="auth-brand-panel">
        <div className="auth-brand-content">
          <BrandMark />
          <div className="auth-statement">
            <span className="eyebrow">今日留白</span>
            <h1>把思绪放在<br />安静的地方。</h1>
          </div>
          <div className="paper-visual" aria-hidden="true">
            <div className="paper-date">07 / 21</div>
            <div className="paper-title">今天值得记下的事</div>
            <div className="paper-rule paper-rule-long" />
            <div className="paper-rule" />
            <div className="paper-rule paper-rule-short" />
            <div className="paper-sign"><Sparkles size={16} /> 保持好奇</div>
          </div>
          <p className="auth-quote">“记录不是为了记住全部，而是为了不丢失重要的部分。”</p>
        </div>
      </section>

      <section className="auth-form-panel">
        <form className="auth-form" onSubmit={submit}>
          <div className="auth-heading">
            <span className="mobile-brand"><BrandMark /></span>
            <h2>欢迎回来</h2>
            <p>登录后继续写下你的想法</p>
          </div>

          <label className="field">
            <span>账号</span>
            <input
              value={account}
              onChange={(event) => setAccount(event.target.value)}
              autoComplete="username"
              minLength={3}
              maxLength={32}
              placeholder="输入账号"
              required
            />
          </label>

          <label className="field">
            <span>密码</span>
            <span className="password-field">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                minLength={8}
                maxLength={128}
                placeholder="输入密码"
                required
              />
              <button
                type="button"
                className="field-icon"
                title={showPassword ? '隐藏密码' : '显示密码'}
                aria-label={showPassword ? '隐藏密码' : '显示密码'}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </span>
          </label>

          <label className="remember-row">
            <input type="checkbox" checked={remember} onChange={(event) => setRemember(event.target.checked)} />
            <span className="checkbox-visual"><Check size={13} /></span>
            <span>记住我 30 天</span>
          </label>

          {error && <div className="form-error" role="alert">{error}</div>}

          <button className="primary-button" type="submit" disabled={busy}>
            {busy && <LoaderCircle className="spin" size={17} />}
            登录
          </button>
        </form>
      </section>
    </main>
  );
}

function NotesWorkspace({
  user,
  onLogout,
  theme,
  setTheme,
}: {
  user: User;
  onLogout: () => void;
  theme: Theme;
  setTheme: (theme: Theme) => void;
}) {
  const [view, setView] = useState<View>('notes');
  const [notes, setNotes] = useState<Note[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const saveTimers = useRef(new Map<string, number>());

  const loadNotes = useCallback(async (nextView: View) => {
    setLoading(true);
    setListError('');
    try {
      const result = await api.notes.list(nextView === 'archive');
      setNotes(result.notes);
      setActiveId((current) => result.notes.some((note) => note.id === current) ? current : result.notes[0]?.id ?? null);
    } catch (error) {
      setListError(error instanceof Error ? error.message : '笔记加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadNotes(view);
  }, [loadNotes, view]);

  useEffect(() => () => {
    for (const timer of saveTimers.current.values()) window.clearTimeout(timer);
  }, []);

  const visibleNotes = useMemo(() => {
    const keyword = search.trim().toLocaleLowerCase();
    return notes.filter((note) => {
      if (note.isArchived !== (view === 'archive')) return false;
      if (view === 'pinned' && !note.isPinned) return false;
      if (!keyword) return true;
      return `${note.title}\n${note.content}`.toLocaleLowerCase().includes(keyword);
    });
  }, [notes, search, view]);

  useEffect(() => {
    if (activeId && !visibleNotes.some((note) => note.id === activeId)) {
      setActiveId(visibleNotes[0]?.id ?? null);
    }
  }, [activeId, visibleNotes]);

  const activeNote = notes.find((note) => note.id === activeId) ?? null;

  async function createNote() {
    setListError('');
    try {
      const { note } = await api.notes.create();
      setSearch('');
      if (view !== 'notes') setView('notes');
      setNotes((current) => [note, ...current.filter((item) => !item.isArchived)]);
      setActiveId(note.id);
      setSaveState('saved');
    } catch (error) {
      setListError(error instanceof Error ? error.message : '新建笔记失败');
    }
  }

  function updateNote(id: string, patch: Partial<Note>) {
    const current = notes.find((note) => note.id === id);
    if (!current) return;
    const optimistic = { ...current, ...patch, updatedAt: Date.now() };
    setNotes((items) => items.map((item) => item.id === id ? optimistic : item));
    setSaveState('saving');

    const existingTimer = saveTimers.current.get(id);
    if (existingTimer) window.clearTimeout(existingTimer);
    const timer = window.setTimeout(async () => {
      try {
        const payload = {
          title: optimistic.title,
          content: optimistic.content,
          isPinned: optimistic.isPinned,
          isArchived: optimistic.isArchived,
        };
        const { note } = await api.notes.update(id, payload);
        setNotes((items) => items.map((item) => item.id === id ? note : item));
        setSaveState('saved');
      } catch (error) {
        setSaveState('error');
        setListError(error instanceof Error ? error.message : '保存失败');
      } finally {
        saveTimers.current.delete(id);
      }
    }, 550);
    saveTimers.current.set(id, timer);
  }

  async function deleteNote(note: Note) {
    if (!window.confirm(`确定永久删除“${note.title || '无标题笔记'}”吗？此操作无法撤销。`)) return;
    const timer = saveTimers.current.get(note.id);
    if (timer) window.clearTimeout(timer);
    saveTimers.current.delete(note.id);
    try {
      await api.notes.remove(note.id);
      setNotes((items) => items.filter((item) => item.id !== note.id));
    } catch (error) {
      setListError(error instanceof Error ? error.message : '删除失败');
    }
  }

  async function logout() {
    try {
      await api.auth.logout();
    } finally {
      onLogout();
    }
  }

  function changeView(nextView: View) {
    setView(nextView);
    setSearch('');
    setActiveId(null);
    setMobileNavOpen(false);
  }

  return (
    <main
      className={`workspace ${activeNote ? 'mobile-editor-open' : ''} ${sidebarOpen ? '' : 'sidebar-collapsed'} ${mobileNavOpen ? 'mobile-nav-open' : ''}`}
      onClick={(event) => {
        if (mobileNavOpen && event.target === event.currentTarget) setMobileNavOpen(false);
      }}
    >
      <aside className="app-sidebar">
        <div className="sidebar-header">
          <BrandMark compact={!sidebarOpen} />
          <button
            className="icon-button sidebar-toggle"
            type="button"
            title={sidebarOpen ? '收起侧栏' : '展开侧栏'}
            aria-label={sidebarOpen ? '收起侧栏' : '展开侧栏'}
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
          </button>
        </div>

        <button className="new-note-button" type="button" onClick={() => void createNote()}>
          <Plus size={18} />
          <span>新建笔记</span>
        </button>

        <nav className="nav-list" aria-label="笔记分类">
          <NavItem icon={<FileText size={18} />} label="全部笔记" active={view === 'notes'} onClick={() => changeView('notes')} />
          <NavItem icon={<Pin size={18} />} label="置顶" active={view === 'pinned'} onClick={() => changeView('pinned')} />
          <NavItem icon={<Archive size={18} />} label="归档" active={view === 'archive'} onClick={() => changeView('archive')} />
        </nav>

        <div className="sidebar-footer">
          <button
            className="profile-button"
            type="button"
            title={`@${user.username}`}
            onClick={() => void logout()}
          >
            <span className="avatar">{user.displayName.slice(0, 1).toUpperCase()}</span>
            <span className="profile-copy">
              <strong>{user.displayName}</strong>
              <small>退出登录</small>
            </span>
            <LogOut className="logout-icon" size={16} />
          </button>
        </div>
      </aside>

      <section className="note-list-pane">
        <header className="note-list-header">
          <div className="mobile-list-title">
            <button className="icon-button mobile-menu" type="button" title="菜单" aria-label="菜单" onClick={() => setMobileNavOpen(true)}>
              <Menu size={19} />
            </button>
            <div>
              <h1>{viewLabel(view)}</h1>
              <span>{visibleNotes.length} 条笔记</span>
            </div>
          </div>
          <div className="desktop-list-title">
            <h1>{viewLabel(view)}</h1>
            <span>{visibleNotes.length} 条</span>
          </div>
          <button
            className="icon-button"
            type="button"
            title={theme === 'light' ? '切换深色模式' : '切换浅色模式'}
            aria-label={theme === 'light' ? '切换深色模式' : '切换浅色模式'}
            onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
          >
            {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
          </button>
        </header>

        <label className="search-box">
          <Search size={17} />
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索笔记" />
          {search && (
            <button type="button" title="清空搜索" aria-label="清空搜索" onClick={() => setSearch('')}>
              <X size={15} />
            </button>
          )}
        </label>

        {listError && <div className="list-error" role="alert">{listError}</div>}

        <div className="note-list">
          {loading ? (
            <div className="list-loading"><LoaderCircle className="spin" size={20} /></div>
          ) : visibleNotes.length ? (
            visibleNotes.map((note) => (
              <NoteListItem
                key={note.id}
                note={note}
                active={note.id === activeId}
                onClick={() => setActiveId(note.id)}
              />
            ))
          ) : (
            <ListEmptyState view={view} searching={Boolean(search)} onCreate={() => void createNote()} />
          )}
        </div>

        <button className="mobile-new-button" type="button" title="新建笔记" aria-label="新建笔记" onClick={() => void createNote()}>
          <Plus size={22} />
        </button>
      </section>

      <section className="editor-pane">
        {activeNote ? (
          <NoteEditor
            note={activeNote}
            saveState={saveState}
            onBack={() => setActiveId(null)}
            onChange={(patch) => updateNote(activeNote.id, patch)}
            onDelete={() => void deleteNote(activeNote)}
          />
        ) : (
          <EditorEmptyState onCreate={() => void createNote()} />
        )}
      </section>
    </main>
  );
}

function NavItem({ icon, label, active, onClick }: { icon: React.ReactNode; label: string; active: boolean; onClick: () => void }) {
  return (
    <button className={`nav-item ${active ? 'active' : ''}`} type="button" onClick={onClick} title={label}>
      {icon}
      <span>{label}</span>
      {active && <ChevronRight className="nav-chevron" size={15} />}
    </button>
  );
}

function NoteListItem({ note, active, onClick }: { note: Note; active: boolean; onClick: () => void }) {
  const preview = note.content.trim().replace(/\s+/g, ' ') || '暂无正文';
  return (
    <button className={`note-list-item ${active ? 'active' : ''}`} type="button" onClick={onClick}>
      <span className="note-item-heading">
        <strong>{note.title.trim() || '无标题笔记'}</strong>
        {note.isPinned && <Pin size={13} fill="currentColor" />}
      </span>
      <span className="note-preview">{preview}</span>
      <time dateTime={new Date(note.updatedAt).toISOString()}>{formatRelativeTime(note.updatedAt)}</time>
    </button>
  );
}

function NoteEditor({
  note,
  saveState,
  onBack,
  onChange,
  onDelete,
}: {
  note: Note;
  saveState: SaveState;
  onBack: () => void;
  onChange: (patch: Partial<Note>) => void;
  onDelete: () => void;
}) {
  return (
    <article className="editor">
      <header className="editor-toolbar">
        <button className="icon-button mobile-back" type="button" title="返回笔记列表" aria-label="返回笔记列表" onClick={onBack}>
          <ArrowLeft size={19} />
        </button>
        <div className={`save-status ${saveState}`}>
          {saveState === 'saving' && <><LoaderCircle className="spin" size={14} /> 保存中</>}
          {saveState === 'saved' && <><Check size={14} /> 已保存</>}
          {saveState === 'error' && '保存失败'}
          {saveState === 'idle' && formatFullDate(note.updatedAt)}
        </div>
        <div className="editor-actions">
          <button
            className={`icon-button ${note.isPinned ? 'active' : ''}`}
            type="button"
            title={note.isPinned ? '取消置顶' : '置顶'}
            aria-label={note.isPinned ? '取消置顶' : '置顶'}
            onClick={() => onChange({ isPinned: !note.isPinned })}
          >
            <Pin size={18} fill={note.isPinned ? 'currentColor' : 'none'} />
          </button>
          <button
            className="icon-button"
            type="button"
            title={note.isArchived ? '移出归档' : '归档'}
            aria-label={note.isArchived ? '移出归档' : '归档'}
            onClick={() => onChange({ isArchived: !note.isArchived })}
          >
            {note.isArchived ? <ArchiveRestore size={18} /> : <Archive size={18} />}
          </button>
          <span className="toolbar-divider" />
          <button className="icon-button danger" type="button" title="永久删除" aria-label="永久删除" onClick={onDelete}>
            <Trash2 size={18} />
          </button>
        </div>
      </header>
      <div className="editor-content">
        <input
          className="note-title-input"
          value={note.title}
          onChange={(event) => onChange({ title: event.target.value })}
          maxLength={160}
          placeholder="无标题笔记"
          aria-label="笔记标题"
        />
        <div className="note-meta">
          <time dateTime={new Date(note.createdAt).toISOString()}>{formatFullDate(note.createdAt)}</time>
          <span />
          <span>{countCharacters(note.content)} 字</span>
        </div>
        <textarea
          className="note-content-input"
          value={note.content}
          onChange={(event) => onChange({ content: event.target.value })}
          maxLength={200_000}
          placeholder="开始写点什么…"
          aria-label="笔记正文"
          spellCheck
        />
      </div>
    </article>
  );
}

function ListEmptyState({ view, searching, onCreate }: { view: View; searching: boolean; onCreate: () => void }) {
  return (
    <div className="list-empty">
      <span className="empty-icon">{searching ? <Search size={20} /> : view === 'archive' ? <Archive size={20} /> : <FileText size={20} />}</span>
      <strong>{searching ? '没有匹配的笔记' : view === 'pinned' ? '还没有置顶笔记' : view === 'archive' ? '归档是空的' : '还没有笔记'}</strong>
      {!searching && view === 'notes' && <button type="button" onClick={onCreate}>写第一条</button>}
    </div>
  );
}

function EditorEmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="editor-empty">
      <span className="empty-sheet"><NotebookPen size={27} /></span>
      <strong>选择一条笔记</strong>
      <p>或创建一页新的留白</p>
      <button className="secondary-button" type="button" onClick={onCreate}><Plus size={16} /> 新建笔记</button>
    </div>
  );
}

function BrandMark({ compact = false }: { compact?: boolean }) {
  return (
    <span className={`brand-mark ${compact ? 'compact' : ''}`}>
      <span className="brand-icon"><NotebookPen size={20} /></span>
      {!compact && <span>留白</span>}
    </span>
  );
}

function viewLabel(view: View) {
  if (view === 'pinned') return '置顶笔记';
  if (view === 'archive') return '归档';
  return '全部笔记';
}

function countCharacters(value: string) {
  return value.replace(/\s/g, '').length;
}

function formatFullDate(timestamp: number) {
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(timestamp);
}

function formatRelativeTime(timestamp: number) {
  const difference = timestamp - Date.now();
  const minutes = Math.round(difference / 60_000);
  if (Math.abs(minutes) < 1) return '刚刚';
  if (Math.abs(minutes) < 60) return new Intl.RelativeTimeFormat('zh-CN', { numeric: 'auto' }).format(minutes, 'minute');
  const hours = Math.round(difference / 3_600_000);
  if (Math.abs(hours) < 24) return new Intl.RelativeTimeFormat('zh-CN', { numeric: 'auto' }).format(hours, 'hour');
  const days = Math.round(difference / 86_400_000);
  if (Math.abs(days) < 7) return new Intl.RelativeTimeFormat('zh-CN', { numeric: 'auto' }).format(days, 'day');
  return new Intl.DateTimeFormat('zh-CN', { month: 'short', day: 'numeric' }).format(timestamp);
}

export default App;
